import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";
import { normalizeResultsResponse } from "features/results/utils/normalizeResultsResponse";

const DEFAULT_LIMIT = 20;

const buildCommonResultsUrl = ({
  tenantVal,
  orderBy,
  messageTypes,
  limit,
  offset,
  searchQuery,
  unassigned,
  unreviewed,
}) => {
  const params = new URLSearchParams();

  if (orderBy) params.set("orderBy", orderBy);
  // Server-side pagination: BE returns this page + a `totalCount`.
  params.set("limit", String(limit));
  params.set("offset", String(offset));
  // Return each result's XSL stylesheet inline so the HL7 preview needs no
  // extra request.
  params.set("includeXsl", "true");
  // Server-side search (BE requires >= 3 chars; the caller enforces this).
  if (searchQuery) params.set("searchQuery", searchQuery);
  // Server-side filters: omit = all, true/false narrow (KIN-1015).
  if (unassigned !== undefined) params.set("unassigned", String(unassigned));
  if (unreviewed !== undefined) params.set("unreviewed", String(unreviewed));
  // Repeated `messageTypes` params — values can contain commas
  // (e.g. "ReAblement, South West Referral Response"), so never comma-join.
  (messageTypes ?? []).forEach((type) => params.append("messageTypes", type));

  return `${tenantVal}/healthlink/common/results?${params.toString()}`;
};

export const useStaffCommonResults = ({
  orderBy,
  messageTypes,
  searchQuery,
  unassigned,
  unreviewed,
  limit = DEFAULT_LIMIT,
  offset = 0,
  enabled = true,
}) => {

  const tenantVal =
    useSelector((state) => state.auth.user?.tenant) ||
    localStorage.getItem("kinesin-tenant");

  return useQuery({
    queryKey: [
      "staffCommonResults",
      orderBy,
      messageTypes,
      searchQuery,
      unassigned,
      unreviewed,
      limit,
      offset,
      tenantVal,
    ],
    queryFn: async () => {
      const axiosInstance = await createAxiosInstance();
      const response = await axiosInstance.get(
        buildCommonResultsUrl({
          tenantVal,
          orderBy,
          messageTypes,
          limit,
          offset,
          searchQuery,
          unassigned,
          unreviewed,
        })
      );

      return normalizeResultsResponse(response.data);
    },
    meta: { errorMessage: "Error fetching staff common results" },
   
    staleTime: 1000,
    enabled,
  });
};
